import { useState, useEffect, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useApiQuery, useApiMutation } from "@/lib/api/hooks"
import { CardRendererRaw } from "@/components/cards/CardRenderer"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { playCorrect, playIncorrect } from "@/lib/sounds"
import { useLessonStore } from "@/stores/lesson-store"
import { Scratchpad } from "@/components/Scratchpad"

const MAX_HEARTS = 5

function Hearts({ count, max }: { count: number; max: number }) {
  return (
    <div className="inline-flex items-center gap-1">
      {Array.from({ length: max }, (_, i) => (
        <svg
          key={i}
          width="20"
          height="18"
          viewBox="0 0 20 18"
          fill={i < count ? "#ef4444" : "none"}
          stroke={i < count ? "#ef4444" : "var(--color-muted-foreground)"}
          strokeWidth="1.5"
          className={cn(
            "transition-all duration-300",
            i === count && count < max && "animate-pulse"
          )}
        >
          <path d="M10 16.5C10 16.5 1.5 11 1.5 5.5C1.5 2.46 3.96 0.5 6.5 0.5C8.24 0.5 9.58 1.42 10 2.5C10.42 1.42 11.76 0.5 13.5 0.5C16.04 0.5 18.5 2.46 18.5 5.5C18.5 11 10 16.5 10 16.5Z" />
        </svg>
      ))}
    </div>
  )
}

export function LessonPage() {
  const { topicId } = useParams<{ topicId: string }>()
  const navigate = useNavigate()
  const { data, isPending } = useApiQuery(
    "get",
    "/topics/{topicId}/lesson",
    { params: { path: { topicId: topicId! } } },
    { enabled: !!topicId }
  )
  const optimizeMutation = useApiMutation("post", "/fsrs/optimize")
  const completeMutation = useApiMutation(
    "post",
    "/topics/{topicId}/lesson/complete",
    {
      onSuccess: (result) => {
        if (result.shouldOptimize) {
          optimizeMutation.mutate({})
        }
      },
    }
  )
  const saveCardMutation = useApiMutation("post", "/card-reviews")

  // Zustand store
  const session = useLessonStore((s) => s.session)
  const startSession = useLessonStore((s) => s.startSession)
  const resumeSession = useLessonStore((s) => s.resumeSession)
  const recordAnswer = useLessonStore((s) => s.recordAnswer)
  const advanceCard = useLessonStore((s) => s.advanceCard)
  const clearSession = useLessonStore((s) => s.clearSession)

  const answered = useRef(false)
  const cardStartTime = useRef(Date.now())
  const initialized = useRef(false)
  const [finished, setFinished] = useState(false)
  const [finalResults, setFinalResults] = useState<{ correct: number; total: number } | null>(null)

  const cards = data?.cards ?? []
  const currentIndex = session?.currentIndex ?? 0
  const hearts = session?.hearts ?? MAX_HEARTS
  const results = session?.results ?? []
  const currentCard = cards[currentIndex]
  const totalCards = cards.length
  const correctCount = results.filter((r) => r.correct).length
  const wiped = hearts <= 0 && results.length > 0

  // Initialize or resume session when data loads
  useEffect(() => {
    if (!data || !topicId || initialized.current) return
    initialized.current = true

    const existingSession = useLessonStore.getState().session

    // Check if Zustand store has a session for this topic
    if (existingSession?.topicId === topicId && existingSession.lessonId === data.lesson.id) {
      // Already have local state — just reset the card timer
      cardStartTime.current = Date.now()
      answered.current = false
      return
    }

    // Check if DB has partial progress (cross-device resume)
    if (data.answeredCardIds.length > 0 && data.answeredCardIds.length < cards.length) {
      const dbResults = data.answeredCardIds.map((cardId) => ({
        cardId,
        correct: true, // we don't have exact per-card results from DB, approximate
        responseTimeMs: 0,
      }))
      resumeSession(topicId, data.lesson.id, data.answeredCardIds.length, data.wrongCount, dbResults)
      cardStartTime.current = Date.now()
      answered.current = false
      return
    }

    // Fresh start
    startSession(topicId, data.lesson.id)
    cardStartTime.current = Date.now()
    answered.current = false
  }, [data, topicId, cards.length, startSession, resumeSession])

  const handleAnswer = useCallback(
    (correct: boolean) => {
      if (!currentCard || answered.current) return
      const elapsed = Date.now() - cardStartTime.current
      const result = { cardId: currentCard.id, correct, responseTimeMs: elapsed }

      answered.current = true
      recordAnswer(result)

      // Save to DB immediately
      saveCardMutation.mutate({
        body: {
          cardId: currentCard.id,
          correct,
          responseTimeMs: elapsed,
        },
      })

      if (correct) {
        playCorrect()
      } else {
        playIncorrect()
      }
    },
    [currentCard, recordAnswer, saveCardMutation]
  )

  function handleContinue() {
    if (currentIndex < totalCards - 1) {
      advanceCard()
      answered.current = false
      cardStartTime.current = Date.now()
    } else {
      finishLesson()
    }
  }

  function finishLesson() {
    // Capture results before clearing the session
    const graded = results.filter((r) => r.cardId).length
    const correct = correctCount
    setFinalResults({ correct, total: graded })
    setFinished(true)
    if (data?.lesson) {
      completeMutation.mutate({
        params: { path: { topicId: topicId! } },
        body: {
          correctCount: correct,
          totalCount: graded,
        },
      })
    }
    clearSession()
  }

  function handleRetry() {
    clearSession()
    if (data?.lesson && topicId) {
      startSession(topicId, data.lesson.id)
    }
    answered.current = false
    cardStartTime.current = Date.now()
  }

  const backUrl = data?.unitId ? `/unit/${data.unitId}` : "/"
  const isTeachCard = currentCard?.type === "teach"
  const currentCardAnswered = currentCard
    ? results.some((r) => r.cardId === currentCard.id)
    : false

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    )
  }

  if (!data || cards.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center flex-col gap-4">
        <p className="text-sm text-muted-foreground">No lesson available for this topic.</p>
        <Button variant="outline" onClick={() => navigate(backUrl)}>
          Go back
        </Button>
      </div>
    )
  }

  // Wiped out
  if (wiped) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-sm w-full text-center space-y-6 px-4">
          <Hearts count={0} max={MAX_HEARTS} />
          <h2 className="text-2xl font-bold">Out of hearts</h2>
          <p className="text-sm text-muted-foreground">
            You lost all your hearts on {data.topicName}. Review the material and try again.
          </p>
          <div className="flex flex-col gap-2 pt-4">
            <Button onClick={handleRetry}>
              Try again
            </Button>
            <Button variant="outline" onClick={() => { clearSession(); navigate(backUrl) }}>
              Back to topics
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Lesson complete
  if (finished && finalResults) {
    const score = finalResults.total > 0 ? Math.round((finalResults.correct / finalResults.total) * 100) : 0
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-sm w-full text-center space-y-6 px-4">
          <Hearts count={hearts} max={MAX_HEARTS} />
          <h2 className="text-2xl font-bold">Lesson Complete</h2>
          <p className="text-muted-foreground">{data.topicName}</p>
          <div className="text-5xl font-bold tabular-nums">
            {score}%
          </div>
          <p className="text-sm text-muted-foreground">
            {finalResults.correct} of {finalResults.total} questions correct
          </p>
          {completeMutation.data && (
            <p className="text-sm">
              Mastery: <span className="font-semibold">{completeMutation.data.masteryScore}/100</span>
            </p>
          )}
          <div className="flex flex-col gap-2 pt-4">
            <Button onClick={() => navigate(backUrl)}>
              Back to topics
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-4 py-3 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate(backUrl)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Exit
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{data.topicName}</p>
        </div>
        <Hearts count={hearts} max={MAX_HEARTS} />
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
          {currentIndex + 1} / {totalCards}
        </span>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-muted shrink-0">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / totalCards) * 100}%` }}
        />
      </div>

      {/* Card area */}
      <main className="flex-1 overflow-y-auto">
        <div className="relative max-w-2xl mx-auto px-4 py-8">
          <Scratchpad resetKey={currentCard?.id} />
          {currentCard && (
            <CardRendererRaw
              key={currentCard.id}
              type={currentCard.type}
              data={currentCard.data as Record<string, unknown>}
              onAnswer={handleAnswer}
            />
          )}
        </div>
      </main>

      {/* Bottom bar */}
      <div className="border-t border-border px-4 py-4 shrink-0">
        <div className="max-w-2xl mx-auto">
          {(isTeachCard || currentCardAnswered) && (
            <Button className="w-full" onClick={handleContinue}>
              {currentIndex < totalCards - 1 ? "Continue" : "Finish"}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
